import React, { useEffect, useRef, useState } from 'react';
import usePlayerDialog from "../usePlayerDialog";
import { createPortal } from 'react-dom';
import PlayerPosition from './PlayerPosition';

export const groupLabel = name => name === 'Goalies' ? 'Målvakter' : name === 'Extra Players' ? 'Övriga spelare' : name.replace(/(\d+)(st|nd|rd|th) Line/, 'Femma $1');
const shortPosition = position => ({Vänsterforward: 'VF', Center: 'C', Högerforward: 'HF', Vänsterback: 'VB', Högerback: 'HB', Forward: 'FW', Back: 'B', Målvakt: 'MV'}[position] || position);

function PlayerProfile({player, onClose, onChange}) {
    const dialog = useRef(null);
    const backdrop = usePlayerDialog();
    useEffect(() => {
        const previous = document.activeElement;
        dialog.current.querySelector('button').focus({ preventScroll: true });
        const handleKey = event => {
            if (event.key === 'Escape') onClose();
            if (event.key !== 'Tab') return;
            const controls = [...dialog.current.querySelectorAll('button, select, summary, a[href]')].filter(element => !element.disabled && element.getClientRects().length);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', handleKey);
        return () => { document.removeEventListener('keydown', handleKey); previous?.focus({ preventScroll: true }); };
    }, [onClose]);
    return createPortal(<div ref={backdrop} className="player-profile-backdrop" role="presentation" onClick={onClose}>
        <section ref={dialog} className="player-profile-widget hockey-player-profile" role="dialog" aria-modal="true" aria-labelledby="player-profile-name" onClick={event => event.stopPropagation()}>
            <button className="player-profile-close" type="button" onClick={onClose} aria-label="Stäng spelarprofil">×</button>
            <div className="player-profile-photo-wrap player-profile-avatar" aria-hidden="true">#{player.number || '–'}</div>
            <div className="player-profile-content">
                <span className="player-profile-kicker">#{player.number} · {player.group}</span>
                <h2 id="player-profile-name">{player.name}</h2>
                <p>{player.team}</p>
            </div>
            {player.position_key && <PlayerPosition player={player} onChange={onChange}/>}
            <span className="player-profile-link player-profile-link--disabled">Officiell profil saknas</span>
        </section>
    </div>, document.body);
}

function PlayerRow({players, onSelect, positioned = false}) {
    if (!players.length) return null;
    return <div className={`hockey-player-grid${positioned ? ' hockey-player-grid--positioned' : ''}`}>
        {players.map((person, index) => <button type="button" className="formation-player hockey-player" key={person.position_key || index}
            style={positioned ? {gridColumn: person.position.startsWith('Vänster') ? 1 : person.position.startsWith('Höger') ? 3 : person.position === 'Center' ? 2 : undefined} : undefined}
            onClick={() => onSelect(person)} title={`Visa profil för ${person.name}`}>
            <span className="formation-player__number">#{person.number}</span>
            <span className="formation-player__name">{person.name}</span>
            {person.position && <span className="formation-player__meta" title={person.position}>{shortPosition(person.position)}</span>}
        </button>)}
    </div>;
}

function GroupPlayers({players, onSelect}) {
    const forwards = players.filter(person => person.position?.includes('forward') || person.position === 'Center');
    const backs = players.filter(person => person.position?.includes('back') || person.position === 'Back');
    const others = players.filter(person => !forwards.includes(person) && !backs.includes(person));
    const order = {Vänsterforward: 1, Vänsterback: 1, Center: 2, Högerforward: 3, Högerback: 3};
    const sorted = list => [...list].sort((left, right) => (order[left.position] || 2) - (order[right.position] || 2));
    return <><PlayerRow players={sorted(forwards)} onSelect={onSelect} positioned/><PlayerRow players={sorted(backs)} onSelect={onSelect} positioned/><PlayerRow players={others} onSelect={onSelect}/></>;
}

export default function HockeyLineups({lineup, logos}) {
    const [player, setPlayer] = useState(null);
    const [changes, setChanges] = useState({});
    const close = React.useCallback(() => setPlayer(null), []);
    if (!lineup?.length) return <p className="events-state">Laguppställning finns inte ännu.</p>;
    const effective = person => Object.prototype.hasOwnProperty.call(changes, person.position_key)
        ? {...person, position: changes[person.position_key] || person.official_position || ''} : person;
    return <><div className="hockey-lineups">
        {lineup.map((team, index) => <section className="team-lineup" key={team.team}>
            <header className="team-lineup-header">
                {logos?.[index] && <img src={logos[index]} alt="" className="team-lineup-logo"/>}
                <div><span className="team-lineup-kicker">Laguppställning</span><h2 className="team-lineup-name">{team.team}</h2></div>
            </header>
            <div className="line-cards">{[...team.groups].sort((left, right) => {
                const rank = group => group.name === 'Goalies' ? 0 : parseInt(group.name, 10) || 99;
                return rank(left) - rank(right);
            }).map(group => <article className={group.name === 'Goalies' ? 'goalkeeper-card' : 'line-card'} key={group.name}>
                <div className={group.name === 'Goalies' ? 'goalkeeper-card__heading' : 'line-card__header'}><h3>{groupLabel(group.name)}</h3></div>
                <GroupPlayers players={group.players.map(effective)} onSelect={person => setPlayer({...person, group: groupLabel(group.name), team: team.team})}/>
            </article>)}</div>
        </section>)}
    </div>{player && <PlayerProfile key={player.position_key} player={effective(player)} onClose={close}
        onChange={position => setChanges(current => ({...current, [player.position_key]: position}))}/>}</>;
}
